import axios from 'axios';
import { auth } from './firebase';

export const API_HOST = process.env.REACT_APP_API_HOST || 'https://portal.learningmasteryasia.com';

// ─── Session-expired event (deduped) ─────────────────────────────────────────
// Fired when any API call returns 401 or 403.
// 401 = missing / invalid token.
// 403 = forbidden (APIGW-level auth failure or missing JWT claims) — reauth required.
// 404 from family endpoints = "no family yet" — handled per-call, NOT here.
let _sessionExpiredFired = false;

function dispatchSessionExpired() {
  if (_sessionExpiredFired) return;
  _sessionExpiredFired = true;
  window.dispatchEvent(new CustomEvent('sessionExpired'));
}

// Called from AuthContext when a fresh user session is confirmed, so the
// modal can appear again if the session expires a second time.
export function resetSessionExpiredFlag() {
  _sessionExpiredFired = false;
}

// ─── Axios instance ───────────────────────────────────────────────────────────
const api = axios.create({ baseURL: API_HOST });

api.interceptors.request.use(async (config) => {
  // Once the session has expired, hard-block every outgoing request so nothing
  // fires in the background while the "Sign In Again" modal is visible.
  if (_sessionExpiredFired) {
    return Promise.reject(Object.assign(new Error('Session expired — sign in required'), { _blocked: true }));
  }
  try {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (_) {}
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (error) => {
    // Don't re-fire for requests we blocked ourselves
    if (error._blocked) return Promise.reject(error);

    const status = error.response?.status;
    if (status === 401 || status === 403) {
      dispatchSessionExpired();
    }
    return Promise.reject(error);
  }
);

export default api;
